'use client';

import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useLenis } from 'lenis/react';
import Image from 'next/image';
import { useRef, useEffect, useLayoutEffect } from 'react';
import Cta from "@/components/cta/cta";
import Grid from "@/components/icon/grid";
import TreeIllustration from "@/components/icon/tree-illustration";
import Section from "@/components/section";
import { ASSET_PREFIX } from '@/constants/routes';
import { useApp } from '@/context/AppContext';


const data = {
  image1: {
    src: ASSET_PREFIX + '/img/about-image-1.webp',
    alt: '',
    width: 894,
    height: 892,
  },
  image2: {
    src: ASSET_PREFIX + '/img/about-image-2.webp',
    alt: '',
    width: 555,
    height: 386,
  },
  image3: {
    src: ASSET_PREFIX + '/img/about-map.webp',
    alt: '',
    width: 531,
    height: 740,
  },
  image4: {
    src: ASSET_PREFIX + '/img/about-record.webp',
    alt: '',
    width: 564,
    height: 630,
  },
  image6: {
    src: ASSET_PREFIX + '/img/about-person-1.webp',
    alt: '',
    width: 406,
    height: 552,
  },
  image7: {
    src: ASSET_PREFIX + '/img/about-person-2.webp',
    alt: '',
    width: 484,
    height: 676,
  },
}

export default function AboutHero({ link, text, title }) {
  const lenis = useLenis();
  const lenisRef = useRef(lenis);
  const { headerHeight, aboutAnimationCompleted, setAboutAnimationCompleted, currentTransitionName, getSavedScrollPosition } = useApp()
  const aboutAnimationCompletedRef = useRef(aboutAnimationCompleted)
  const hasRestoredScroll = useRef(false);
  const hasLayoutShifted = useRef(false);

  useLayoutEffect(() => {
    if (hasLayoutShifted.current) {
      return
    }

    hasLayoutShifted.current = true
    history.scrollRestoration = 'manual';
    document.documentElement.scrollTop = 0;
  }, []);

  useEffect(() => {
    if (lenis) {
      lenisRef.current = lenis;
      lenis.stop();
    }
  }, [lenis]);

  // Restore scroll position when returning from story overlay
  useEffect(() => {
    if (currentTransitionName === 'overlay-out' && !hasRestoredScroll.current) {
      const savedPosition = getSavedScrollPosition();
      if (savedPosition !== null) {
        hasRestoredScroll.current = true;
        window.scrollTo(0, savedPosition);
      }
    }
    // Reset flag when transition changes away from overlay-out
    if (currentTransitionName !== 'overlay-out') {
      hasRestoredScroll.current = false;
    }
  }, [currentTransitionName, getSavedScrollPosition]);

  const sectionRef = useRef(null);
  const heroRef = useRef(null);
  const contentRef = useRef(null);
  const galleryRef = useRef(null);
  const gridRef = useRef(null);
  const firstImage = useRef(null);
  const secondImage = useRef(null);
  const thirdImage = useRef(null);
  const fourthImage = useRef(null);

  const firstPerson = useRef(null);
  const secondPerson = useRef(null);
  const firstPersonImage = useRef(null);
  const secondPersonImage = useRef(null);
  const treeIllustration = useRef(null);
  const enterTimelineRef = useRef(null);

  // Parallax wrapper refs (separate from image refs so transforms don't conflict)
  const firstImageParallax = useRef(null);
  const secondImageParallax = useRef(null);
  const thirdImageParallax = useRef(null);
  const fourthImageParallax = useRef(null);

  const firstPersonParallax = useRef(null);
  const secondPersonParallax = useRef(null);
  const treeIllustrationParallax = useRef(null);

  const enter = () => {
    if (enterTimelineRef.current) {
      enterTimelineRef.current.kill();
    }

    const tl = gsap.timeline({
      defaults: { ease: 'power3.out' },
      onComplete: () => {
        lenisRef.current?.start();
        setAboutAnimationCompleted(true);

        // Create parallax for person images after enter completes (to capture entered y as start)
        [firstPersonImage, secondPersonImage].forEach((ref) => {
          if (ref.current) {
            gsap.to(ref.current, {
              y: 0,
              ease: 'none',
              force3D: true,
              scrollTrigger: {
                trigger: sectionRef.current,
                start: 'top top',
                end: 'bottom top',
                scrub: true,
              },
            });
          }
        });
      },
    });
    enterTimelineRef.current = tl;

    tl.addLabel('start');

    // Fade in grid
    tl.to(gridRef.current, {
      opacity: 1,
      duration: 3,
    }, 'start');

    // Animate first image
    tl.set(firstImage.current, { opacity: 1 }, 0);
    tl.to(firstImage.current, {
      y: 0,
      yPercent: -50,
      rotate: 8,
      duration: 1,
    }, 'start+=0.4');

    // Animate second image
    tl.set(secondImage.current, { opacity: 1 }, 'start+=1');
    tl.to(secondImage.current, {
      x: 0,
      y: 0,
      rotate: 6,
      duration: 1,
    }, 'start+=1');

    // Animate third image
    tl.set(thirdImage.current, { opacity: 1 }, 'start+=1.1');
    tl.to(thirdImage.current, {
      x: 0,
      y: 0,
      rotate: -6,
      duration: 1,
    }, 'start+=1.1');

    // Animate fourth image
    tl.set(fourthImage.current, { opacity: 1 }, 'start+=0.95');
    tl.to(fourthImage.current, {
      x: 0,
      y: 0,
      duration: 1,
    }, 'start+=0.95');

    // Animate tree illustration
    tl.set(treeIllustration.current, { opacity: 1 }, 'start+=1');
    tl.to(treeIllustration.current, {
      x: 0,
      y: 0,
      duration: 1,
    }, 'start+=1');

    // Animate first person
    tl.to(firstPerson.current, {
      opacity: 1,
      y: 0,
      duration: 1,
    }, 'start+=1.4');
    tl.to(firstPersonImage.current, {
      y: 30,
      duration: 1,
    }, 'start+=1.4');

    // Animate second person
    tl.to(secondPerson.current, {
      opacity: 1,
      y: 0,
      duration: 1,
    }, 'start+=1.6');
    tl.to(secondPersonImage.current, {
      y: 30,
      duration: 1,
    }, 'start+=1.6');

    // Animate gallery down to its original position
    tl.to(galleryRef.current, {
      ease: 'expo.inOut',
      y: 0,
      duration: 1.4,
    }, '-=1.1');

    // Fade in content
    tl.to(
      contentRef.current,
      {
        opacity: 1,
        y: 0,
        duration: 0.5,
        onStart: () => contentRef.current.classList.add("is-revealed"),
      },
      "-=0.7",
    );
  };

  const enterSimple = () => {
    if (enterTimelineRef.current) {
      enterTimelineRef.current.kill();
    }

    const tl = gsap.timeline({
      defaults: { ease: 'expo.out' },
      onComplete: () => {
        lenisRef.current?.start();
      },
    });
    enterTimelineRef.current = tl;

    // Show all gallery children that have opacity-0
    gsap.set([
      gridRef.current,
      firstImage.current,
      secondImage.current,
      thirdImage.current,
      fourthImage.current,
      treeIllustration.current,
      firstPerson.current,
      secondPerson.current,
    ], { opacity: 1 });

    // Reset first image to its resting position
    gsap.set(firstImage.current, { y: 0, yPercent: -50 });

    tl.addLabel('start');

    tl.fromTo(
      contentRef.current,
      {
        opacity: 0,
        y: 150,
      },
      {
        opacity: 1,
        y: 0,
        duration: 1.2,
        onStart: () => contentRef.current.classList.add("is-revealed"),
      },
      "start+=0.2",
    );

    tl.fromTo(galleryRef.current, {
      opacity: 0,
      y: 150,
    }, {
      opacity: 1,
      y: 0,
      duration: 1.2,
    }, 'start+=0.3');
  };

  useGSAP(() => {
    const mm = gsap.matchMedia();

    // Calculate offset to center gallery in viewport
    const galleryRect = galleryRef.current.getBoundingClientRect();
    const galleryCenter = galleryRef.current.offsetTop + galleryRect.height / 2;
    const viewportCenter = window.innerHeight / 2;
    const offsetY = viewportCenter - galleryCenter - headerHeight;

    // Helper to calculate offset to center an element in gallery
    const getCenterOffset = (element) => {
      const rect = element.getBoundingClientRect();
      const centerX = rect.left - galleryRect.left + rect.width / 2;
      const centerY = rect.top - galleryRect.top + rect.height / 2;
      return {
        x: galleryRect.width / 2 - centerX,
        y: galleryRect.height / 2 - centerY,
      };
    };

    const secondImageOffset = getCenterOffset(secondImage.current);
    const thirdImageOffset = getCenterOffset(thirdImage.current);
    const fourthImageOffset = getCenterOffset(fourthImage.current);
    const treeIllustrationOffset = getCenterOffset(treeIllustration.current);

    mm.add('(prefers-reduced-motion: reduce)', () => {
      // Show all elements immediately without animation
      gsap.set(galleryRef.current, { opacity: 1 });
      gsap.set(gridRef.current, { opacity: 1 });
      gsap.set(firstImage.current, { opacity: 1 });
      gsap.set(secondImage.current, { opacity: 1 });
      gsap.set(thirdImage.current, { opacity: 1 });
      gsap.set(fourthImage.current, { opacity: 1 });
      gsap.set(treeIllustration.current, { opacity: 1 });
      gsap.set(firstPerson.current, { opacity: 1 });
      gsap.set(secondPerson.current, { opacity: 1 });
      gsap.set(contentRef.current, { opacity: 1 });
    });

    mm.add('(prefers-reduced-motion: no-preference)', () => {
      if (aboutAnimationCompletedRef.current) {
        // Simple fade in + move up for return visits
        gsap.set(contentRef.current, { opacity: 0, y: 40 });
        gsap.set(galleryRef.current, { opacity: 0, y: 40 });

        ScrollTrigger.create({
          trigger: heroRef.current,
          start: 'top bottom',
          onEnter: enterSimple,
        });
      } else {
        // Set initial states for full enter animation
        gsap.set(gridRef.current, { opacity: 0 });
        gsap.set(contentRef.current, { opacity: 0, y: -90 });
        gsap.set(galleryRef.current, { y: offsetY, opacity: 1 });
        gsap.set(firstImage.current, { y: window.innerHeight, yPercent: -50, rotate: -8 });
        gsap.set(secondImage.current, { x: secondImageOffset.x, y: secondImageOffset.y, rotate: -10 });
        gsap.set(thirdImage.current, { x: thirdImageOffset.x, y: thirdImageOffset.y, rotate: -25 });
        gsap.set(fourthImage.current, { x: fourthImageOffset.x, y: fourthImageOffset.y });
        gsap.set(treeIllustration.current, { x: treeIllustrationOffset.x, y: treeIllustrationOffset.y, opacity: 0 });
        gsap.set(firstPerson.current, { y: 60 });
        gsap.set(secondPerson.current, { y: 60 });
        gsap.set(firstPersonImage.current, { y: 60 });
        gsap.set(secondPersonImage.current, { y: 60 });

        ScrollTrigger.create({
          trigger: heroRef.current,
          start: 'top bottom',
          onEnter: enter,
        });
      }

      // Parallax scrolling on wrapper elements (won't conflict with enter animation on images)
      const parallaxElements = [
        { ref: gridRef, y: -40 },
        { ref: firstImageParallax, y: 120 },
        { ref: secondImageParallax, y: 10 },
        { ref: thirdImageParallax, y: -20 },
        { ref: fourthImageParallax, y: 40 },

        { ref: firstPersonParallax, y: 70 },
        { ref: secondPersonParallax, y: 40 },
        { ref: treeIllustrationParallax, y: 10 },
      ];

      parallaxElements.forEach(({ ref, y }) => {
        if (ref.current) {
          gsap.to(ref.current, {
            y,
            ease: 'none',
            force3D: true,
            scrollTrigger: {
              trigger: sectionRef.current,
              start: 'top top',
              end: 'bottom top',
              scrub: true,
            },
          });
        }
      });
    });

    return () => {
      if (enterTimelineRef.current) {
        enterTimelineRef.current.kill();
        enterTimelineRef.current = null;

        gsap.set([
          gridRef.current,
          contentRef.current,
          galleryRef.current,
          firstImage.current,
          secondImage.current,
          thirdImage.current,
          fourthImage.current,
          treeIllustration.current,
          firstPerson.current,
          secondPerson.current,
          firstPersonImage.current,
          secondPersonImage.current
        ], {
          clearProps: 'all'
        })
      }
    };
  }, { dependencies: [headerHeight], revertOnUpdate: true });

  return (
    <Section ref={sectionRef} theme="main-light" className="pt-header pb-8.75 lg:pb-34.25" isTopSection={true}>
      <div ref={heroRef} className="grid-7 xl:pt-9 mx-auto">
        <div ref={contentRef} className="col-span-full text-center opacity-0">
          <h1 className="mx-auto max-w-[84.75%] heading-1-small xl:heading-1 lg:max-w-none">{title}</h1>
          <p className="mx-auto mt-8 heading-5-small xl:heading-5 lg:mt-10 lg:max-w-148 text-pretty">{text}</p>
          <Cta href={link.href} className="col-span-full w-fit mt-8 mx-auto cta-big lg:mt-7.5" hasArrow={true}>{link.label}</Cta>
        </div>

        <div ref={galleryRef} className="col-span-full relative mt-8 lg:mt-9 opacity-0" aria-hidden="true">
          <Grid ref={gridRef} className="opacity-0 will-change-transform" />

          <div ref={firstImageParallax} className="absolute z-8 w-[66.1%] top-1/2 left-1/2 -translate-x-1/2 lg:w-[42.33%] will-change-transform">
            <Image unoptimized={true} ref={firstImage} className="opacity-0 rotate-8" {...data.image1} priority />
          </div>
          <div ref={secondImageParallax} className="max-lg:hidden absolute z-7 w-[23.6%] -top-[4%] -right-[4.1%] will-change-transform">
            <Image unoptimized={true} ref={secondImage} className="opacity-0 rotate-6" {...data.image2} />
          </div>
          <div ref={thirdImageParallax} className="max-lg:hidden absolute z-6 w-[22.6%] bottom-[10%] -left-[3%] will-change-transform">
            <Image unoptimized={true} ref={thirdImage} className="opacity-0 -rotate-6" {...data.image3} />
          </div>
          <div ref={fourthImageParallax} className="absolute z-5 w-[50%] bottom-0 left-0 lg:w-[24%] lg:left-[16%] will-change-transform">
            <Image unoptimized={true} ref={fourthImage} className="opacity-0" {...data.image4} priority />
          </div>

          <div ref={firstPersonParallax} className="absolute z-6 w-[45%] h-[36%] top-[5.87%] -left-[2.82%] lg:w-50.5 lg:h-50.5 lg:top-[7.5%] lg:left-[5.94%] will-change-transform">
            <div ref={firstPerson} className="opacity-0 relative w-full h-full">
              <div className="bg-main-green absolute inset-0 rounded-[50%]" />
              <div className="absolute bottom-0 left-0 w-full h-[120%] rounded-b-[132px] overflow-hidden lg:w-50.5 lg:h-69 lg:rounded-b-[202px]">
                <Image unoptimized={true} ref={firstPersonImage} className="absolute bottom-0 left-1/2 -translate-x-1/2 w-full h-auto will-change-transform" {...data.image6} />
              </div>
            </div>
          </div>

          <div ref={secondPersonParallax} className="absolute z-6 bottom-[3.38%] -right-[3.11%] w-[45%] h-[36%] lg:w-50.5 lg:h-50.5 lg:bottom-[20.44%] lg:right-[7.72%] will-change-transform">
            <div ref={secondPerson} className="opacity-0 relative w-full h-full">
              <div className="bg-[#4294D4] absolute inset-0 rounded-[50%]" />
              <div className="absolute bottom-0 left-0 w-full h-[120%] rounded-b-[132px] overflow-hidden lg:w-50.5 lg:h-69 lg:rounded-b-[202px]">
                <Image unoptimized={true} ref={secondPersonImage} className="absolute bottom-0 left-1/2 -translate-x-1/2 w-full h-auto will-change-transform" {...data.image7} />
              </div>
            </div>
          </div>

          <div ref={treeIllustrationParallax} className="absolute w-[33%] top-[6.32%] right-[6.9%] z-6 lg:w-50.75 lg:top-[unset] lg:-bottom-[4%] lg:right-[20.7%] will-change-transform">
            <TreeIllustration ref={treeIllustration} />
          </div>
        </div>
      </div>
    </Section>
  )
}
